import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Upload, X, Layers, Flame, GripVertical, ArrowUpDown, Tag } from "lucide-react";
import { api } from "@/api/client";
import { useManageBusiness } from "@/pages/manage/ManageShell";
import { AuthImage } from "@/components/AuthImage";
import { money, priceLabel } from "@/lib/pricing";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";

const empty = (businessId, cats) => ({
  business_id: businessId,
  name: "",
  category: cats?.[0]?.key ?? "",
  description: "",
  price: 0,
  image_url: "",
  sizes: [],
  flavors: [],
  available: true,
  hidden: false,
  pricing_mode: "single",
  price_sizes: [],
  price_tiers: [],
  deposit_percent_override: null,
});

export default function Products() {
  const { businessId } = useManageBusiness();
  const [products, setProducts] = useState([]);
  const [cats, setCats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [catsOpen, setCatsOpen] = useState(false);
  const [reordering, setReordering] = useState(false);
  const [order, setOrder] = useState([]);
  const [dragIndex, setDragIndex] = useState(null);
  const [savingOrder, setSavingOrder] = useState(false);

  const catLabel = (key) => cats.find((c) => c.key === key)?.label || (key ? key : "Uncategorized");

  const loadCats = () =>
    api.listBusinesses().then((bizs) => {
      const biz = (bizs || []).find((b) => b.id === businessId);
      setCats((biz?.categories || []).filter((c) => c.key !== "all"));
    }).catch(() => {});

  const reload = () => {
    setLoading(true);
    api.listProducts(businessId, null, true).then(setProducts).catch(() => {}).finally(() => setLoading(false));
    loadCats();
  };
  useEffect(reload, [businessId]);

  const startReorder = () => { setOrder(products); setReordering(true); };
  const cancelReorder = () => { setReordering(false); setDragIndex(null); };

  const onDrop = (toIndex) => {
    if (dragIndex === null || dragIndex === toIndex) { setDragIndex(null); return; }
    setOrder((prev) => {
      const next = [...prev];
      const [moved] = next.splice(dragIndex, 1);
      next.splice(toIndex, 0, moved);
      return next;
    });
    setDragIndex(null);
  };

  const saveOrder = async () => {
    setSavingOrder(true);
    try {
      await api.reorderProducts(businessId, order.map((p) => p.id));
      toast.success("Order saved");
      setProducts(order);
      setReordering(false);
    } catch (err) {
      toast.error(err.message);
    } finally {
      setSavingOrder(false);
    }
  };

  const remove = async (p) => {
    if (!window.confirm(`Delete "${p.name}"?`)) return;
    try {
      await api.deleteProduct(p.id);
      toast.success("Deleted");
      reload();
    } catch (err) {
      toast.error(err.message);
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <h1 className="font-display text-3xl" style={{ color: "var(--ink)" }}>Products</h1>
        <div className="flex gap-2">
          {reordering ? (
            <>
              <button onClick={cancelReorder} data-testid="reorder-cancel-btn" className="px-4 py-2 rounded-full text-sm font-semibold" style={{ border: "1px solid var(--border)", color: "var(--ink)" }}>
                Cancel
              </button>
              <button onClick={saveOrder} disabled={savingOrder} data-testid="reorder-save-btn" className="btn-brand px-4 py-2 rounded-full text-sm font-semibold inline-flex items-center gap-1.5 disabled:opacity-60">
                {savingOrder ? "Saving…" : "Save order"}
              </button>
            </>
          ) : (
            <>
              <button onClick={startReorder} data-testid="reorder-btn" disabled={products.length < 2} className="px-4 py-2 rounded-full text-sm font-semibold inline-flex items-center gap-1.5 disabled:opacity-50" style={{ border: "1px solid var(--border)", color: "var(--ink)" }}>
                <ArrowUpDown size={16} /> Reorder
              </button>
              <button onClick={() => setCatsOpen(true)} data-testid="manage-categories-btn" className="px-4 py-2 rounded-full text-sm font-semibold inline-flex items-center gap-1.5" style={{ border: "1px solid var(--border)", color: "var(--ink)" }}>
                <Tag size={16} /> Categories
              </button>
              <button onClick={() => setBulkOpen(true)} data-testid="bulk-add-btn" className="px-4 py-2 rounded-full text-sm font-semibold inline-flex items-center gap-1.5" style={{ border: "1px solid var(--border)", color: "var(--ink)" }}>
                <Layers size={16} /> Bulk add
              </button>
              <button onClick={() => setEditing(empty(businessId, cats))} data-testid="add-product-btn" className="btn-brand px-4 py-2 rounded-full text-sm font-semibold inline-flex items-center gap-1.5">
                <Plus size={16} /> New product
              </button>
            </>
          )}
        </div>
      </div>

      {reordering && (
        <p className="mt-2 text-sm" style={{ color: "var(--muted)" }}>
          Drag products to set the order shoppers see in the <strong>All items</strong> view, then Save.
        </p>
      )}

      {loading ? (
        <div className="mt-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">{Array.from({ length: 6 }).map((_, i) => <div key={i} className="h-56 rounded-2xl animate-pulse" style={{ background: "var(--surface-2)" }} />)}</div>
      ) : reordering ? (
        <div className="mt-6 space-y-2" data-testid="reorder-list">
          {order.map((p, i) => (
            <div
              key={p.id}
              data-testid={`reorder-row-${p.id}`}
              draggable
              onDragStart={() => setDragIndex(i)}
              onDragOver={(e) => e.preventDefault()}
              onDrop={() => onDrop(i)}
              className="flex items-center gap-3 rounded-xl p-2.5 cursor-move transition"
              style={{ background: dragIndex === i ? "var(--surface-2)" : "var(--surface)", border: "1px solid var(--border)", opacity: dragIndex === i ? 0.6 : 1 }}
            >
              <GripVertical size={18} style={{ color: "var(--muted)" }} className="shrink-0" />
              <span className="w-6 text-center text-sm font-bold tabular-nums" style={{ color: "var(--muted)" }}>{i + 1}</span>
              <div className="h-12 w-12 rounded-lg overflow-hidden shrink-0" style={{ background: "var(--surface-2)" }}>
                <AuthImage src={p.image_url} alt={p.name} className="h-full w-full object-cover" />
              </div>
              <div className="min-w-0 flex-1">
                <div className="font-semibold truncate" style={{ color: "var(--ink)" }}>{p.name}</div>
                <div className="text-xs uppercase tracking-wide" style={{ color: "var(--muted)" }}>{catLabel(p.category)}</div>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="mt-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {products.map((p) => {
            const label = priceLabel(p);
            return (
              <div key={p.id} data-testid={`admin-product-${p.id}`} className="rounded-2xl overflow-hidden" style={{ background: "var(--surface)", border: "1px solid var(--border)" }}>
                <div className="aspect-[4/3]" style={{ background: "var(--surface-2)" }}>
                  <AuthImage src={p.image_url} alt={p.name} className="h-full w-full object-cover" />
                </div>
                <div className="p-4">
                  <div className="flex items-start justify-between gap-2">
                    <div>
                      <h3 className="font-display text-lg" style={{ color: "var(--ink)" }}>{p.name}</h3>
                      <div className="text-xs uppercase tracking-wide" style={{ color: "var(--muted)" }}>{catLabel(p.category)} · {p.pricing_mode}</div>
                    </div>
                    {p.hidden ? (
                      <span className="text-[10px] font-bold px-1.5 py-0.5 rounded" style={{ background: "#eef0f3", color: "#52525b" }}>HIDDEN</span>
                    ) : !p.available ? (
                      <span className="text-[10px] font-bold px-1.5 py-0.5 rounded" style={{ background: "#fdecec", color: "#b91c1c" }}>SOLD OUT</span>
                    ) : null}
                  </div>
                  <div className="mt-2 font-display text-lg" style={{ color: "var(--brand)" }}>
                    {label.prefix ? `${label.prefix} ` : ""}{money(label.value)}
                  </div>
                  <div className="mt-3 flex gap-2">
                    <button onClick={() => setEditing(p)} data-testid={`edit-product-${p.id}`} className="flex-1 rounded-lg py-2 text-sm font-semibold inline-flex items-center justify-center gap-1.5" style={{ border: "1px solid var(--border)", color: "var(--ink)" }}><Pencil size={14} /> Edit</button>
                    <button onClick={() => remove(p)} data-testid={`delete-product-${p.id}`} className="rounded-lg py-2 px-3 text-sm font-semibold text-red-600" style={{ border: "1px solid #fca5a5" }}><Trash2 size={14} /></button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {editing && <ProductForm initial={editing} businessId={businessId} cats={cats} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); reload(); }} />}
      {bulkOpen && <BulkAddForm businessId={businessId} cats={cats} onClose={() => setBulkOpen(false)} onSaved={() => { setBulkOpen(false); reload(); }} />}
      {catsOpen && <CategoriesDialog businessId={businessId} cats={cats} onClose={() => setCatsOpen(false)} onChanged={reload} />}
    </div>
  );
}

const emptyRow = (cats) => ({
  name: "",
  category: cats?.[0]?.key ?? "",
  price: "",
  description: "",
  image_url: "",
  _uploading: false,
});

function BulkAddForm({ businessId, cats, onClose, onSaved }) {
  const [rows, setRows] = useState([emptyRow(cats), emptyRow(cats), emptyRow(cats)]);
  const [saving, setSaving] = useState(false);
  const inputStyle = { background: "var(--surface)", border: "1px solid var(--border)" };

  const setRow = (i, patch) => setRows((prev) => prev.map((r, x) => (x === i ? { ...r, ...patch } : r)));
  const addRow = () => setRows((prev) => [...prev, emptyRow(cats)]);
  const removeRow = (i) => setRows((prev) => (prev.length === 1 ? prev : prev.filter((_, x) => x !== i)));

  const upload = async (i, e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setRow(i, { _uploading: true });
    try {
      const res = await api.uploadImage(file);
      setRow(i, { image_url: res.url, _uploading: false });
    } catch (err) {
      toast.error(err.message || "Upload failed");
      setRow(i, { _uploading: false });
    }
  };

  const saveAll = async () => {
    const filled = rows.filter((r) => r.name.trim());
    if (filled.length === 0) { toast.error("Add at least one product with a name"); return; }
    const missingImg = filled.find((r) => !r.image_url);
    if (missingImg) { toast.error(`"${missingImg.name}" needs an image`); return; }
    const products = filled.map((r) => ({
      business_id: businessId,
      name: r.name.trim(),
      category: r.category,
      description: r.description.trim(),
      price: parseFloat(r.price) || 0,
      image_url: r.image_url,
      sizes: [],
      flavors: [],
      pricing_mode: "single",
      price_sizes: [],
      price_tiers: [],
      deposit_percent_override: null,
    }));
    setSaving(true);
    try {
      const created = await api.createProductsBulk(products);
      toast.success(`Added ${created.length} product${created.length === 1 ? "" : "s"}`);
      onSaved();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setSaving(false);
    }
  };

  const anyUploading = rows.some((r) => r._uploading);
  const readyCount = rows.filter((r) => r.name.trim() && r.image_url).length;

  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl flex items-center gap-2">
            <Flame size={20} style={{ color: "var(--brand)" }} /> Bulk add products
          </DialogTitle>
          <DialogDescription>
            Stack several products and save them all at once. You can fine-tune sizes, tiers &amp; quotes later by editing each one.
          </DialogDescription>
        </DialogHeader>

        <div className="mt-4 space-y-3">
          {rows.map((r, i) => (
            <div key={i} data-testid={`bulk-row-${i}`} className="rounded-xl p-3 flex gap-3 items-start" style={{ background: "var(--surface-2)", border: "1px solid var(--border)" }}>
              <label className="h-16 w-16 rounded-lg overflow-hidden shrink-0 cursor-pointer flex items-center justify-center" style={{ background: "var(--surface)", border: "1px solid var(--border)" }} title="Upload image">
                {r.image_url ? (
                  <AuthImage src={r.image_url} alt="" className="h-full w-full object-cover" />
                ) : (
                  <Upload size={16} style={{ color: "var(--muted)" }} />
                )}
                <input type="file" accept="image/*" onChange={(e) => upload(i, e)} data-testid={`bulk-image-${i}`} className="hidden" />
              </label>
              <div className="flex-1 min-w-0 space-y-2">
                <div className="flex gap-2">
                  <input value={r.name} onChange={(e) => setRow(i, { name: e.target.value })} data-testid={`bulk-name-${i}`} placeholder="Product name" className="flex-1 rounded-lg px-3 py-2 outline-none text-sm" style={inputStyle} />
                  <select value={r.category} onChange={(e) => setRow(i, { category: e.target.value })} data-testid={`bulk-category-${i}`} className="rounded-lg px-2 py-2 outline-none text-sm" style={inputStyle}>
                    {cats.map((c) => <option key={c.key} value={c.key}>{c.label}</option>)}
                  </select>
                  <input type="number" value={r.price} onChange={(e) => setRow(i, { price: e.target.value })} data-testid={`bulk-price-${i}`} placeholder="$" className="w-20 rounded-lg px-2 py-2 outline-none text-sm" style={inputStyle} />
                </div>
                <input value={r.description} onChange={(e) => setRow(i, { description: e.target.value })} data-testid={`bulk-desc-${i}`} placeholder="Short description (optional)" className="w-full rounded-lg px-3 py-2 outline-none text-sm" style={inputStyle} />
                {r._uploading && <span className="text-xs" style={{ color: "var(--muted)" }}>Uploading image…</span>}
              </div>
              <button type="button" onClick={() => removeRow(i)} className="p-2 text-red-500 shrink-0" data-testid={`bulk-remove-${i}`} title="Remove row"><X size={16} /></button>
            </div>
          ))}
        </div>

        <button onClick={addRow} data-testid="bulk-add-row-btn" className="mt-3 w-full rounded-xl py-2.5 text-sm font-semibold inline-flex items-center justify-center gap-1.5" style={{ border: "1px dashed var(--border)", color: "var(--brand)" }}>
          <Plus size={16} /> Add another row
        </button>

        <button onClick={saveAll} disabled={saving || anyUploading || readyCount === 0} data-testid="bulk-save-btn" className="btn-brand w-full mt-4 rounded-xl py-3 font-semibold disabled:opacity-60">
          {saving ? "Saving…" : `Save ${readyCount || ""} product${readyCount === 1 ? "" : "s"}`.trim()}
        </button>
      </DialogContent>
    </Dialog>
  );
}

function ProductForm({ initial, businessId, cats, onClose, onSaved }) {
  const [f, setF] = useState({
    ...initial,
    sizesText: (initial.sizes || []).join(", "),
    flavorsText: (initial.flavors || []).join(", "),
  });
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [sideProducts, setSideProducts] = useState([]);
  const isNew = !initial.id;
  const set = (k, v) => setF((prev) => ({ ...prev, [k]: v }));

  const cfg = f.sides_config || { enabled: false, required_count: 0, side_ids: [] };
  const setCfg = (patch) => set("sides_config", { ...cfg, ...patch });
  const showSides = businessId === "rubys";

  useEffect(() => {
    if (!showSides) return;
    api.listProducts(businessId, "sides", true).then(setSideProducts).catch(() => {});
  }, [businessId, showSides]);

  const toggleSideId = (id) => {
    const cur = cfg.side_ids || [];
    setCfg({ side_ids: cur.includes(id) ? cur.filter((x) => x !== id) : [...cur, id] });
  };

  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const res = await api.uploadImage(file);
      set("image_url", res.url);
      toast.success("Image uploaded");
    } catch (err) {
      toast.error(err.message || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const addSizeRow = () => set("price_sizes", [...(f.price_sizes || []), { name: "", price: 0, quote: false }]);
  const addEtcRow = () => set("price_sizes", [...(f.price_sizes || []), { name: "Etc.", price: 0, quote: true }]);
  const addTierRow = () => set("price_tiers", [...(f.price_tiers || []), { min_qty: 1, max_qty: null, unit_price: 0 }]);

  const save = async () => {
    if (!f.name.trim()) { toast.error("Name is required"); return; }
    if (!f.image_url) { toast.error("Please add an image"); return; }
    const body = {
      business_id: businessId,
      name: f.name.trim(),
      category: f.category,
      description: f.description,
      price: parseFloat(f.price) || 0,
      image_url: f.image_url,
      sizes: f.sizesText.split(",").map((s) => s.trim()).filter(Boolean),
      flavors: f.flavorsText.split(",").map((s) => s.trim()).filter(Boolean),
      flavor_images: pruneMap(f.flavor_images, f.flavorsText.split(",").map((s) => s.trim()).filter(Boolean)),
      size_images: pruneMap(f.size_images, (f.pricing_mode === "sizes" ? (f.price_sizes || []).map((s) => s.name) : f.sizesText.split(",")).map((s) => s.trim()).filter(Boolean)),
      pricing_mode: f.pricing_mode,
      price_sizes: f.pricing_mode === "sizes" ? (f.price_sizes || []).map((s) => ({ name: s.name, price: s.quote ? 0 : parseFloat(s.price) || 0, quote: !!s.quote })) : [],
      price_tiers: f.pricing_mode === "tiers" ? (f.price_tiers || []).map((t) => ({ min_qty: parseInt(t.min_qty) || 1, max_qty: t.max_qty === "" || t.max_qty == null ? null : parseInt(t.max_qty), unit_price: parseFloat(t.unit_price) || 0 })) : [],
      deposit_percent_override: f.deposit_percent_override === "" || f.deposit_percent_override == null ? null : parseFloat(f.deposit_percent_override),
      sides_config: showSides && cfg.enabled
        ? { enabled: true, required_count: parseInt(cfg.required_count) || 0, side_ids: cfg.side_ids || [] }
        : { enabled: false, required_count: 0, side_ids: [] },
      ...(isNew ? {} : { available: f.available, hidden: f.hidden }),
    };
    setSaving(true);
    try {
      if (isNew) await api.createProduct(body);
      else await api.updateProduct(initial.id, body);
      toast.success(isNew ? "Product created" : "Product updated");
      onSaved();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setSaving(false);
    }
  };

  const inputStyle = { background: "var(--surface)", border: "1px solid var(--border)" };

  // 3-state visibility derived from available + hidden.
  const visState = f.hidden ? "hidden" : f.available ? "shown" : "sold_out";
  const setVis = (state) => {
    if (state === "shown") setF((p) => ({ ...p, hidden: false, available: true }));
    else if (state === "sold_out") setF((p) => ({ ...p, hidden: false, available: false }));
    else setF((p) => ({ ...p, hidden: true }));
  };

  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl">{isNew ? "New product" : "Edit product"}</DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          {/* Image */}
          <div>
            <label className="text-sm font-semibold" style={{ color: "var(--ink)" }}>Image</label>
            <div className="mt-1.5 flex items-center gap-3">
              <div className="h-20 w-20 rounded-xl overflow-hidden shrink-0" style={{ background: "var(--surface-2)" }}>
                {f.image_url && <AuthImage src={f.image_url} alt="" className="h-full w-full object-cover" />}
              </div>
              <label className="cursor-pointer rounded-xl px-4 py-2 text-sm font-semibold inline-flex items-center gap-2" style={inputStyle}>
                <Upload size={15} /> {uploading ? "Uploading…" : "Upload"}
                <input type="file" accept="image/*" onChange={upload} data-testid="product-image-input" className="hidden" />
              </label>
            </div>
          </div>

          <Field label="Name"><input value={f.name} onChange={(e) => set("name", e.target.value)} data-testid="product-name" className="w-full rounded-xl px-3 py-2 outline-none" style={inputStyle} /></Field>

          <div className="grid grid-cols-2 gap-3">
            <Field label="Category">
              <select value={f.category} onChange={(e) => set("category", e.target.value)} data-testid="product-category" className="w-full rounded-xl px-3 py-2 outline-none" style={inputStyle}>
                {!cats.some((c) => c.key === f.category) && <option value={f.category}>{f.category ? f.category : "Uncategorized"}</option>}
                {cats.map((c) => <option key={c.key} value={c.key}>{c.label}</option>)}
              </select>
            </Field>
            <Field label="Base price ($)"><input type="number" value={f.price} onChange={(e) => set("price", e.target.value)} data-testid="product-price" className="w-full rounded-xl px-3 py-2 outline-none" style={inputStyle} /></Field>
          </div>

          <Field label="Description"><textarea value={f.description} onChange={(e) => set("description", e.target.value)} rows={2} data-testid="product-description" className="w-full rounded-xl px-3 py-2 outline-none resize-none" style={inputStyle} /></Field>

          <Field label="Pricing mode">
            <div className="grid grid-cols-3 gap-2 p-1 rounded-xl" style={{ background: "var(--surface-2)" }}>
              {["single", "sizes", "tiers"].map((m) => (
                <button key={m} type="button" onClick={() => set("pricing_mode", m)} data-testid={`pricing-${m}`} className="py-1.5 rounded-lg text-sm font-semibold capitalize" style={f.pricing_mode === m ? { background: "var(--surface)", color: "var(--ink)" } : { color: "var(--muted)" }}>{m}</button>
              ))}
            </div>
          </Field>

          {f.pricing_mode === "sizes" && (
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-sm font-semibold" style={{ color: "var(--ink)" }}>Size prices</span>
                <div className="flex gap-3">
                  <button type="button" onClick={addEtcRow} data-testid="add-etc-size-btn" className="text-sm font-semibold" style={{ color: "var(--accent)" }}>+ Etc. (quote)</button>
                  <button type="button" onClick={addSizeRow} className="text-sm font-semibold" style={{ color: "var(--brand)" }}>+ Add</button>
                </div>
              </div>
              {(f.price_sizes || []).map((s, i) => (
                <div key={i} className="flex gap-2 items-center">
                  <input value={s.name} onChange={(e) => { const arr = [...f.price_sizes]; arr[i] = { ...arr[i], name: e.target.value }; set("price_sizes", arr); }} placeholder="Size name" className="flex-1 rounded-lg px-3 py-2 outline-none text-sm" style={inputStyle} />
                  {s.quote ? (
                    <span className="w-24 text-center text-xs font-semibold px-2 py-2 rounded-lg" style={{ background: "var(--accent-soft)", color: "var(--ink)" }}>On request</span>
                  ) : (
                    <input type="number" value={s.price} onChange={(e) => { const arr = [...f.price_sizes]; arr[i] = { ...arr[i], price: e.target.value }; set("price_sizes", arr); }} placeholder="$" className="w-24 rounded-lg px-3 py-2 outline-none text-sm" style={inputStyle} />
                  )}
                  <label className="flex items-center gap-1 text-xs" style={{ color: "var(--muted)" }} title="Price discussed with customer">
                    <input type="checkbox" checked={!!s.quote} onChange={(e) => { const arr = [...f.price_sizes]; arr[i] = { ...arr[i], quote: e.target.checked }; set("price_sizes", arr); }} data-testid={`size-quote-${i}`} /> quote
                  </label>
                  <button type="button" onClick={() => set("price_sizes", f.price_sizes.filter((_, x) => x !== i))} className="p-2 text-red-500"><X size={16} /></button>
                </div>
              ))}
              <p className="text-xs" style={{ color: "var(--muted)" }}>Tick &quot;quote&quot; (or use + Etc.) for a custom size with no fixed price — you&apos;ll agree the price with the customer.</p>
            </div>
          )}

          {f.pricing_mode === "tiers" && (
            <div className="space-y-2">
              <div className="flex items-center justify-between"><span className="text-sm font-semibold" style={{ color: "var(--ink)" }}>Quantity tiers</span><button type="button" onClick={addTierRow} className="text-sm font-semibold" style={{ color: "var(--brand)" }}>+ Add</button></div>
              {(f.price_tiers || []).map((t, i) => (
                <div key={i} className="flex gap-2 items-center">
                  <input type="number" value={t.min_qty} onChange={(e) => { const arr = [...f.price_tiers]; arr[i] = { ...arr[i], min_qty: e.target.value }; set("price_tiers", arr); }} placeholder="Min" className="w-16 rounded-lg px-2 py-2 outline-none text-sm" style={inputStyle} />
                  <span style={{ color: "var(--muted)" }}>–</span>
                  <input type="number" value={t.max_qty ?? ""} onChange={(e) => { const arr = [...f.price_tiers]; arr[i] = { ...arr[i], max_qty: e.target.value }; set("price_tiers", arr); }} placeholder="Max (∞)" className="w-20 rounded-lg px-2 py-2 outline-none text-sm" style={inputStyle} />
                  <input type="number" value={t.unit_price} onChange={(e) => { const arr = [...f.price_tiers]; arr[i] = { ...arr[i], unit_price: e.target.value }; set("price_tiers", arr); }} placeholder="$/ea" className="w-20 rounded-lg px-2 py-2 outline-none text-sm" style={inputStyle} />
                  <button type="button" onClick={() => set("price_tiers", f.price_tiers.filter((_, x) => x !== i))} className="p-1.5 text-red-500"><X size={16} /></button>
                </div>
              ))}
            </div>
          )}

          <div className="grid grid-cols-2 gap-3">
            <Field label="Sizes (labels, comma-sep)"><input value={f.sizesText} onChange={(e) => set("sizesText", e.target.value)} placeholder='6", 8"' className="w-full rounded-xl px-3 py-2 outline-none text-sm" style={inputStyle} /></Field>
            <Field label="Flavors (comma-sep)"><input value={f.flavorsText} onChange={(e) => set("flavorsText", e.target.value)} placeholder="Vanilla, Chocolate" className="w-full rounded-xl px-3 py-2 outline-none text-sm" style={inputStyle} /></Field>
          </div>

          {(() => {
            const flavorList = f.flavorsText.split(",").map((s) => s.trim()).filter(Boolean);
            const sizeList = (f.pricing_mode === "sizes" ? (f.price_sizes || []).map((s) => s.name) : f.sizesText.split(",")).map((s) => s.trim()).filter(Boolean);
            if (flavorList.length === 0 && sizeList.length === 0) return null;
            return (
              <div className="rounded-xl p-4 space-y-3" style={{ background: "var(--surface-2)" }} data-testid="variant-images-section">
                <div>
                  <div className="text-sm font-semibold" style={{ color: "var(--ink)" }}>Variant photos (optional)</div>
                  <p className="text-xs mt-0.5" style={{ color: "var(--muted)" }}>Add a photo for a flavor or size — it swaps in when a customer picks that option.</p>
                </div>
                {flavorList.map((fl) => (
                  <VariantImageRow key={`fl-${fl}`} mapKey="flavor_images" name={fl} url={f.flavor_images?.[fl]} setF={setF} testid={`flavor-image-${fl}`} />
                ))}
                {sizeList.map((sz) => (
                  <VariantImageRow key={`sz-${sz}`} mapKey="size_images" name={sz} url={f.size_images?.[sz]} setF={setF} testid={`size-image-${sz}`} />
                ))}
              </div>
            );
          })()}

          <Field label="Deposit % override (blank = business default)">
            <input type="number" value={f.deposit_percent_override ?? ""} onChange={(e) => set("deposit_percent_override", e.target.value)} data-testid="deposit-override" placeholder="e.g. 25 for wedding cakes" className="w-full rounded-xl px-3 py-2 outline-none text-sm" style={inputStyle} />
          </Field>

          {showSides && (
            <div className="rounded-xl p-4" style={{ background: "var(--surface-2)" }}>
              <label className="flex items-center gap-2 text-sm font-semibold cursor-pointer" style={{ color: "var(--ink)" }}>
                <input type="checkbox" checked={!!cfg.enabled} onChange={(e) => setCfg({ enabled: e.target.checked })} data-testid="sides-enabled-toggle" />
                Let customers choose sides
              </label>
              {cfg.enabled && (
                <div className="mt-3 space-y-3">
                  <Field label="How many sides must the customer pick?">
                    <input type="number" min={0} value={cfg.required_count ?? 0} onChange={(e) => setCfg({ required_count: e.target.value })} data-testid="sides-required-count" className="w-24 rounded-xl px-3 py-2 outline-none" style={inputStyle} />
                  </Field>
                  <div>
                    <div className="text-sm font-semibold mb-1.5" style={{ color: "var(--ink)" }}>Which sides?</div>
                    <p className="text-xs mb-2" style={{ color: "var(--muted)" }}>Leave all unchecked to offer <strong>every</strong> item in the Sides category. Each side adds its own price.</p>
                    <div className="space-y-1.5 max-h-48 overflow-y-auto">
                      {sideProducts.map((s) => (
                        <label key={s.id} className="flex items-center justify-between gap-2 rounded-lg px-3 py-2 cursor-pointer" style={{ background: "var(--surface)", border: "1px solid var(--border)" }}>
                          <span className="flex items-center gap-2 text-sm" style={{ color: "var(--ink)" }}>
                            <input type="checkbox" checked={(cfg.side_ids || []).includes(s.id)} onChange={() => toggleSideId(s.id)} data-testid={`sides-opt-${s.id}`} />
                            {s.name}
                          </span>
                          <span className="text-xs font-semibold" style={{ color: "var(--brand)" }}>{priceLabel(s).value != null ? money(priceLabel(s).value) : ""}</span>
                        </label>
                      ))}
                      {sideProducts.length === 0 && <p className="text-xs" style={{ color: "var(--muted)" }}>No items in the Sides category yet. Add some first.</p>}
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {!isNew && (
            <Field label="Customer visibility">
              <div className="grid grid-cols-3 gap-2 p-1 rounded-xl" style={{ background: "var(--surface-2)" }}>
                {[
                  { key: "shown", label: "Shown" },
                  { key: "sold_out", label: "Sold out" },
                  { key: "hidden", label: "Hidden" },
                ].map((o) => (
                  <button key={o.key} type="button" onClick={() => setVis(o.key)} data-testid={`visibility-${o.key}`} className="py-1.5 rounded-lg text-sm font-semibold" style={visState === o.key ? { background: "var(--surface)", color: "var(--ink)" } : { color: "var(--muted)" }}>{o.label}</button>
                ))}
              </div>
              <p className="text-xs mt-1.5" style={{ color: "var(--muted)" }}>
                {visState === "shown" ? "Visible to customers and available to order." : visState === "sold_out" ? "Visible with a SOLD OUT badge; customers can't order it." : "Completely removed from the customer's shop."}
              </p>
            </Field>
          )}

          <button onClick={save} disabled={saving || uploading} data-testid="save-product-btn" className="btn-brand w-full rounded-xl py-3 font-semibold disabled:opacity-60">
            {saving ? "Saving…" : isNew ? "Create product" : "Save changes"}
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function CategoriesDialog({ businessId, cats, onClose, onChanged }) {
  const [list, setList] = useState(cats);
  const [newLabel, setNewLabel] = useState("");
  const [busy, setBusy] = useState(false);
  const [editKey, setEditKey] = useState(null);
  const [editLabel, setEditLabel] = useState("");
  const inputStyle = { background: "var(--surface)", border: "1px solid var(--border)" };

  const refresh = async () => {
    const bizs = await api.listBusinesses();
    const biz = (bizs || []).find((b) => b.id === businessId);
    setList((biz?.categories || []).filter((c) => c.key !== "all"));
    onChanged?.();
  };

  const add = async () => {
    if (!newLabel.trim()) return;
    setBusy(true);
    try {
      await api.addCategory(businessId, newLabel.trim());
      setNewLabel("");
      await refresh();
      toast.success("Category added");
    } catch (err) {
      toast.error(err?.response?.data?.detail || err.message);
    } finally {
      setBusy(false);
    }
  };

  const rename = async (key) => {
    if (!editLabel.trim()) { setEditKey(null); return; }
    setBusy(true);
    try {
      await api.renameCategory(businessId, key, editLabel.trim());
      setEditKey(null);
      await refresh();
      toast.success("Category renamed");
    } catch (err) {
      toast.error(err?.response?.data?.detail || err.message);
    } finally {
      setBusy(false);
    }
  };

  const remove = async (c) => {
    if (!window.confirm(`Delete "${c.label}"? Products in it stay but become uncategorized.`)) return;
    setBusy(true);
    try {
      await api.deleteCategory(businessId, c.key);
      await refresh();
      toast.success("Category deleted");
    } catch (err) {
      toast.error(err?.response?.data?.detail || err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl flex items-center gap-2">
            <Tag size={20} style={{ color: "var(--brand)" }} /> Categories
          </DialogTitle>
          <DialogDescription>
            Add, rename, or remove the categories customers browse by. Deleting one keeps its products (they become uncategorized).
          </DialogDescription>
        </DialogHeader>

        <div className="mt-4 space-y-2" data-testid="categories-list">
          {list.map((c) => (
            <div key={c.key} data-testid={`category-row-${c.key}`} className="flex items-center gap-2 rounded-xl p-2.5" style={{ background: "var(--surface-2)", border: "1px solid var(--border)" }}>
              {editKey === c.key ? (
                <>
                  <input value={editLabel} onChange={(e) => setEditLabel(e.target.value)} data-testid={`category-edit-input-${c.key}`} className="flex-1 rounded-lg px-3 py-1.5 outline-none text-sm" style={inputStyle} />
                  <button onClick={() => rename(c.key)} disabled={busy} data-testid={`category-save-${c.key}`} className="btn-brand rounded-lg px-3 py-1.5 text-sm font-semibold">Save</button>
                  <button onClick={() => setEditKey(null)} className="rounded-lg px-2 py-1.5 text-sm" style={{ color: "var(--muted)" }}>Cancel</button>
                </>
              ) : (
                <>
                  <span className="flex-1 font-semibold" style={{ color: "var(--ink)" }}>{c.label}</span>
                  <button onClick={() => { setEditKey(c.key); setEditLabel(c.label); }} data-testid={`category-rename-${c.key}`} className="p-1.5 rounded-lg" style={{ color: "var(--ink)" }}><Pencil size={14} /></button>
                  <button onClick={() => remove(c)} disabled={busy} data-testid={`category-delete-${c.key}`} className="p-1.5 rounded-lg text-red-600"><Trash2 size={14} /></button>
                </>
              )}
            </div>
          ))}
          {list.length === 0 && <p className="text-sm" style={{ color: "var(--muted)" }}>No categories yet. Add your first below.</p>}
        </div>

        <div className="mt-4 flex gap-2">
          <input value={newLabel} onChange={(e) => setNewLabel(e.target.value)} onKeyDown={(e) => e.key === "Enter" && add()} data-testid="new-category-input" placeholder="New category name" className="flex-1 rounded-xl px-3 py-2 outline-none text-sm" style={inputStyle} />
          <button onClick={add} disabled={busy || !newLabel.trim()} data-testid="add-category-submit" className="btn-brand rounded-xl px-4 py-2 text-sm font-semibold inline-flex items-center gap-1.5 disabled:opacity-60">
            <Plus size={16} /> Add
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function VariantImageRow({ mapKey, name, url, setF, testid }) {
  const [busy, setBusy] = useState(false);
  const onPick = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true);
    try {
      const res = await api.uploadImage(file);
      setF((prev) => ({ ...prev, [mapKey]: { ...(prev[mapKey] || {}), [name]: res.url } }));
      toast.success(`Photo set for "${name}"`);
    } catch (err) {
      toast.error(err.message || "Upload failed");
    } finally {
      setBusy(false);
    }
  };
  const clear = () => setF((prev) => {
    const next = { ...(prev[mapKey] || {}) };
    delete next[name];
    return { ...prev, [mapKey]: next };
  });
  return (
    <div className="flex items-center gap-3">
      <div className="h-12 w-12 rounded-lg overflow-hidden shrink-0" style={{ background: "var(--surface)", border: "1px solid var(--border)" }}>
        {url && <AuthImage src={url} alt="" className="h-full w-full object-cover" />}
      </div>
      <div className="flex-1 min-w-0 text-sm font-semibold truncate" style={{ color: "var(--ink)" }}>{name}</div>
      {url && (
        <button type="button" onClick={clear} className="text-xs font-semibold text-red-500 px-2">Remove</button>
      )}
      <label className="cursor-pointer rounded-lg px-3 py-1.5 text-xs font-semibold inline-flex items-center gap-1.5" style={{ background: "var(--surface)", border: "1px solid var(--border)", color: "var(--ink)" }}>
        <Upload size={13} /> {busy ? "…" : url ? "Change" : "Add photo"}
        <input type="file" accept="image/*" onChange={onPick} data-testid={testid} className="hidden" />
      </label>
    </div>
  );
}

function pruneMap(map, keys) {
  return Object.fromEntries(Object.entries(map || {}).filter(([k]) => keys.includes(k)));
}

function Field({ label, children }) {
  return (
    <div>
      <label className="text-sm font-semibold block mb-1.5" style={{ color: "var(--ink)" }}>{label}</label>
      {children}
    </div>
  );
}
